import { useAuth } from 'react-oidc-context'
import { BrandLockup } from '../../brand/BrandLockup'

export interface LoginScreenProps {
  /** Present when `react-oidc-context` surfaced an error (e.g. the callback exchange failed). */
  errorMessage?: string
}

/**
 * ADR-0048 (Phase A, #7) — the real login screen. Shown by `<AuthGuard>` for any unauthenticated
 * route. The "Sign in" CTA starts a genuine Authorization-Code+PKCE redirect to the demo Keycloak
 * (`react-oidc-context`'s `auth.signinRedirect()`, backed by the shared `oidcUserManager`) — never a
 * client-side-only "looks logged in" state. Graphite theme, Poppins, tokens.css only (KB-2) —
 * no literal hex, no emoji.
 */
export function LoginScreen({ errorMessage }: LoginScreenProps) {
  const auth = useAuth()

  return (
    <main
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        padding: 'var(--iw-space-6)',
        background: 'var(--iw-bg)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          background: 'var(--iw-surface)',
          border: '1px solid var(--iw-border)',
          borderRadius: 'var(--iw-radius-lg)',
          boxShadow: 'var(--iw-shadow)',
          padding: 'var(--iw-space-6)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--iw-space-4)',
          textAlign: 'center',
        }}
      >
        <div>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--iw-size-xs)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--iw-text-subtle)',
              fontWeight: 'var(--iw-weight-medium)' as never,
              textAlign: 'center',
            }}
          >
            Certainti.Ai
          </p>
          {/* `aria-label` pins the accessible name to the plain "IncentiWise" wordmark —
              `BrandLockup` renders "Incenti"/"Wise" as two separately-coloured <span>s, and the
              accessible-name (accname) algorithm inserts a space between sibling elements' text
              regardless of actual DOM whitespace; without this, a screen reader would announce
              "Incenti Wise" rather than the one-word brand name. */}
          <h1 aria-label="IncentiWise" style={{ margin: 'var(--iw-space-1) 0 0' }}>
            <BrandLockup />
          </h1>
        </div>
        <p style={{ color: 'var(--iw-text-muted)', margin: 0, fontSize: 'var(--iw-size-sm)' }}>
          Sign in to continue to your workspace.
        </p>
        {errorMessage ? (
          <p
            role="alert"
            style={{
              margin: 0,
              color: 'var(--iw-danger)',
              fontSize: 'var(--iw-size-sm)',
            }}
          >
            {errorMessage}
          </p>
        ) : null}
        <button
          type="button"
          className="iw-btn iw-btn--primary"
          style={{ width: '100%', justifyContent: 'center' }}
          disabled={auth.isLoading}
          onClick={() => {
            void auth.signinRedirect()
          }}
        >
          Sign in
        </button>
      </div>
    </main>
  )
}
